import type { LiveSheetAttribute } from "../../types/generated/live";
import { signed } from "./format";
import { interpolate, type Labels } from "./labels";

// The terms whose title is the canonical `game_terms` pair.
const GAME_TERM: Record<string, string> = {
	ac: "armor_class",
	bac: "attack_bonus_melee",
	bad: "attack_bonus_ranged",
	jpd: "saving_throw_dexterity",
	jpc: "saving_throw_constitution",
	jps: "saving_throw_wisdom",
	movement: "movement",
	initiative: "initiative",
};

export type TermInfo = { title: string; text?: string };

export function termInfo(labels: Pick<Labels, "glossary" | "terms">, key: string, fallbackTitle = key): TermInfo {
	const entry = labels.glossary[key];
	const termKey = GAME_TERM[key];
	const term = termKey ? labels.terms[termKey] : undefined;
	const title = entry?.title ?? (term ? `${term.full} (${term.abbr})` : fallbackTitle);
	return { title, text: entry?.text };
}

// An attribute's name opens the book's description and the test it makes; its modifier opens where the modifier comes from.
export function attributeTerm(attribute: LiveSheetAttribute, labels: Labels): TermInfo & { lines: string[] } {
	const name = labels.attribute_names[attribute.key];
	const test = attribute.score == null ? [] : [`${interpolate(labels.descriptions.test_of, { attribute: name })}: 1d20 ≤ ${attribute.score}`];
	return { title: `${name} (${labels.stats[attribute.key]})`, text: labels.attribute_descriptions[attribute.key], lines: test };
}

export function modifierTerm(attribute: LiveSheetAttribute, labels: Labels): TermInfo & { lines: string[] } {
	const name = labels.attribute_names[attribute.key];
	const value = interpolate(labels.boxes.modifier, { value: signed(attribute.modifier) });
	return {
		title: interpolate(labels.boxes.modifier_title, { attribute: name }),
		lines: attribute.score == null ? [] : [`${labels.stats[attribute.key]} ${attribute.score} → ${value}`],
	};
}
